import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog';
import { BagTransportTerm } from '../bag-transport-term.model';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { map, filter, debounceTime, distinctUntilChanged, switchMap, tap, finalize } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Result } from '@/reservation/reservation.model';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface User {
	id: number;
	email: string;
	firstName: string;
	lastName: string;
}

@Component({
	selector: 'app-transport-assign-dialog',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		MatOptionModule,
		MatButtonModule,
		MatDialogActions,
		MatDialogContent,
		MatDialogTitle,
		MatProgressSpinnerModule,
	],
	templateUrl: './transport-assign-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportAssignDialogComponent implements OnInit {
	readonly dialogData = inject<Pick<BagTransportTerm, 'id' | 'responsibleUser'>>(MAT_DIALOG_DATA);

	readonly searchControl = new FormControl<User | string>(null, [Validators.required]);
	readonly allUsers = signal<User[]>([]);
	readonly userIsSelected = signal(false);
	readonly loader = signal(false);

	private readonly httpClient = inject(HttpClient);
	private readonly destroyRef = inject(DestroyRef);
	private readonly infoboxService = inject(InfoboxService);
	private readonly dialogRef = inject(MatDialogRef);

	ngOnInit(): void {
		this.initSearch();

		if (!!this.dialogData.responsibleUser) {
			this.searchControl.patchValue(this.dialogData.responsibleUser as User);
		}
	}

	displayFn(item: User): string {
		return !!item ? `${item?.firstName} ${item?.lastName}` : '';
	}

	assignMe(): void {
		this.loader.set(true);

		this.httpClient
			.get<User>(`${environment.apiUrl}/profile/`)
			.pipe(finalize(() => this.loader.set(false)))
			.subscribe((profile) => {
				this.assignUser(profile);
			});
	}

	assignUser(userToAssign: User | string): void {
		if (typeof userToAssign === 'string') {
			return;
		}

		this.loader.set(true);

		this.httpClient
			.patch<BagTransportTerm>(`${environment.apiUrl}/transport/transport_term/${this.dialogData.id}/`, {
				responsibleUser: userToAssign,
			})
			.pipe(finalize(() => this.loader.set(false)))
			.subscribe((transport) => {
				this.infoboxService.success(
					`Pomyślnie przypisano ${transport.responsibleUser.firstName} ${transport.responsibleUser.lastName} jako osobę odpowiedzialną za transport ${transport.id}`,
				);
				this.dialogRef.close(true);
			});
	}

	private initSearch(): void {
		this.searchControl.valueChanges
			.pipe(
				tap(() => this.userIsSelected.set(typeof this.searchControl.value === 'string')),
				map((value) => (typeof value === 'string' ? value.trim() : '')),
				filter((value) => value.length >= 3),
				debounceTime(300),
				distinctUntilChanged(),
				switchMap((query) => this.search(query)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((results) => {
				this.allUsers.set(results);
			});
	}

	private search(query: string) {
		return this.httpClient
			.get(`${environment.apiUrl}/profile/all_users/?full_name=${encodeURIComponent(query)}`)
			.pipe(map((result: Result<User>) => result.results));
	}
}
